import React, { useState, useEffect } from 'react';
import { adminFetch } from '../../utils/adminAuth';
import './SubadminPanel.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002';

const SubadminPanel = ({ event, onClose }) => {
  const [subadmins, setSubadmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAddress, setNewAddress] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newPermissions, setNewPermissions] = useState({
    canEditEvent: true,
    canViewAnalytics: true,
    canManageTicketTypes: false,
    canRefund: false,
    canPauseSales: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (event) {
      loadSubadmins();
    }
  }, [event]);

  const loadSubadmins = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await adminFetch(`${API_BASE_URL}/api/subadmins/event/${event.id}`);
      const data = await response.json();
      if (data.success) {
        setSubadmins(data.data);
      } else {
        setError(data.error || 'Failed to load subadmins');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubadmin = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const response = await adminFetch(`${API_BASE_URL}/api/subadmins`, {
        method: 'POST',
        body: JSON.stringify({
          address: newAddress,
          eventId: event.id,
          label: newLabel,
          permissions: newPermissions,
        }),
      });

      const data = await response.json();
      if (data.success) {
        setSubadmins([...subadmins, data.data]);
        setShowAddForm(false);
        setNewAddress('');
        setNewLabel('');
        setNewPermissions({
          canEditEvent: true,
          canViewAnalytics: true,
          canManageTicketTypes: false,
          canRefund: false,
          canPauseSales: false,
        });
      } else {
        setError(data.error || 'Failed to add subadmin');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePermission = async (subadminId, permission, currentValue) => {
    try {
      const response = await adminFetch(`${API_BASE_URL}/api/subadmins/${subadminId}`, {
        method: 'PUT',
        body: JSON.stringify({
          permissions: { [permission]: !currentValue },
        }),
      });

      const data = await response.json();
      if (data.success) {
        setSubadmins(subadmins.map(s =>
          s.id === subadminId ? data.data : s
        ));
      } else {
        setError(data.error || 'Failed to update permission');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggleActive = async (subadminId, currentActive) => {
    try {
      const response = await adminFetch(`${API_BASE_URL}/api/subadmins/${subadminId}`, {
        method: 'PUT',
        body: JSON.stringify({ active: !currentActive }),
      });

      const data = await response.json();
      if (data.success) {
        setSubadmins(subadmins.map(s =>
          s.id === subadminId ? data.data : s
        ));
      } else {
        setError(data.error || 'Failed to toggle status');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRemoveSubadmin = async (subadminId) => {
    if (!window.confirm('Are you sure you want to remove this subadmin?')) {
      return;
    }

    try {
      const response = await adminFetch(`${API_BASE_URL}/api/subadmins/${subadminId}`, {
        method: 'DELETE',
      });

      const data = await response.json();
      if (data.success) {
        setSubadmins(subadmins.filter(s => s.id !== subadminId));
      } else {
        setError(data.error || 'Failed to remove subadmin');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const formatAddress = (address) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="subadmin-panel">
      <div className="subadmin-header">
        <h2>Event Managers</h2>
        <p className="event-name">{event?.name}</p>
        <button className="btn-close" onClick={onClose}>Close</button>
      </div>

      {error && (
        <div className="error-message">{error}</div>
      )}

      <div className="subadmin-actions">
        <button
          className="btn-add-subadmin"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          {showAddForm ? 'Cancel' : '+ Add Manager'}
        </button>
      </div>

      {showAddForm && (
        <form className="add-subadmin-form" onSubmit={handleAddSubadmin}>
          <div className="form-group">
            <label>Wallet Address</label>
            <input
              type="text"
              value={newAddress}
              onChange={(e) => setNewAddress(e.target.value)}
              placeholder="0x..."
              required
            />
          </div>

          <div className="form-group">
            <label>Label (optional)</label>
            <input
              type="text"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="e.g., Event Coordinator, Marketing Team"
            />
          </div>

          <div className="form-group">
            <label>Permissions</label>
            <div className="permissions-grid">
              {Object.entries(newPermissions).map(([key, value]) => (
                <label key={key} className="permission-checkbox">
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) => setNewPermissions({
                      ...newPermissions,
                      [key]: e.target.checked,
                    })}
                  />
                  <span>{key.replace(/can/g, '').replace(/([A-Z])/g, ' $1').trim()}</span>
                </label>
              ))}
            </div>
          </div>

          <button type="submit" className="btn-submit" disabled={saving}>
            {saving ? 'Adding...' : 'Add Manager'}
          </button>
        </form>
      )}

      {loading ? (
        <div className="loading">Loading managers...</div>
      ) : subadmins.length === 0 ? (
        <div className="empty-state">
          <p>No managers assigned to this event yet.</p>
          <p>Add managers to allow them to help manage this event.</p>
        </div>
      ) : (
        <div className="subadmins-list">
          {subadmins.map((subadmin) => (
            <div key={subadmin.id} className={`subadmin-card ${!subadmin.active ? 'inactive' : ''}`}>
              <div className="subadmin-info">
                <div className="address-row">
                  <span className="address" title={subadmin.address}>
                    {formatAddress(subadmin.address)}
                  </span>
                  {subadmin.label && (
                    <span className="label">{subadmin.label}</span>
                  )}
                  <span className={`status ${subadmin.active ? 'active' : 'inactive'}`}>
                    {subadmin.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="meta">
                  Added {formatDate(subadmin.assignedAt)}
                </div>
              </div>

              <div className="permissions-section">
                <span className="permissions-label">Permissions:</span>
                <div className="permissions-tags">
                  {Object.entries(subadmin.permissions).map(([key, value]) => (
                    <button
                      key={key}
                      className={`permission-tag ${value ? 'enabled' : 'disabled'}`}
                      onClick={() => handleTogglePermission(subadmin.id, key, value)}
                      title={`Click to ${value ? 'disable' : 'enable'}`}
                    >
                      {key.replace(/can/g, '').replace(/([A-Z])/g, ' $1').trim()}
                    </button>
                  ))}
                </div>
              </div>

              <div className="subadmin-actions-row">
                <button
                  className={`btn-toggle-status ${subadmin.active ? 'deactivate' : 'activate'}`}
                  onClick={() => handleToggleActive(subadmin.id, subadmin.active)}
                >
                  {subadmin.active ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  className="btn-remove"
                  onClick={() => handleRemoveSubadmin(subadmin.id)}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SubadminPanel;
